import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Download, Store } from 'lucide-react';
import AuditCreditBadge from '@/components/billing/AuditCreditBadge';

export const AUDIT_PLAN_INCLUDES = {
  title: 'Your Account Health Audit includes',
  note: 'Live dashboard monitoring (Overview, Signals, Alerts, Metrics) requires Protego Monitor.',
  items: [
    'Connect your Amazon account & profile',
    'Downloadable Account Health Audit PDF',
    'Enforcement Risk Score in the report',
    'Specialist walkthrough (as included in your purchase)',
  ],
};

export default function AuditPlanHomeCard({
  selectedStoreLabel,
  hasSelection = false,
  downloading = false,
  onDownloadAudit,
  upgradeHref = '/settings#plans',
  onUpgradeClick,
  showAuditCredit = false,
}) {
  return (
    <Card className="bg-card border-[hsl(188,100%,55%)]/25" data-testid="audit-plan-home-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{AUDIT_PLAN_INCLUDES.title}</CardTitle>
        <CardDescription className="text-xs">
          {selectedStoreLabel
            ? `Selected store: ${selectedStoreLabel}`
            : AUDIT_PLAN_INCLUDES.note}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {selectedStoreLabel && (
          <p className="text-xs text-muted-foreground">{AUDIT_PLAN_INCLUDES.note}</p>
        )}
        <ul className="space-y-2">
          {AUDIT_PLAN_INCLUDES.items.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-[hsl(142,72%,45%)] shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" asChild>
            <Link to="/accounts">
              <Store className="w-4 h-4 mr-2" />
              Amazon Accounts
            </Link>
          </Button>
          {onDownloadAudit ? (
            <Button
              type="button"
              size="sm"
              className="grad-accent text-white border-0"
              disabled={downloading || !hasSelection}
              onClick={onDownloadAudit}
              data-testid="audit-plan-download-button"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? 'Generating…' : 'Download audit report'}
            </Button>
          ) : (
            <Button type="button" size="sm" className="grad-accent text-white border-0" asChild>
              <Link to={hasSelection ? '/audit-report' : '/accounts'}>
                <Download className="w-4 h-4 mr-2" />
                {hasSelection ? 'View audit report' : 'Select store for audit'}
              </Link>
            </Button>
          )}
          {onUpgradeClick ? (
            <div className="relative inline-flex flex-col items-stretch">
              {showAuditCredit && (
                <div className="absolute -top-3 right-0 z-10 rotate-6">
                  <AuditCreditBadge />
                </div>
              )}
              <Button type="button" variant="secondary" size="sm" onClick={onUpgradeClick}>
                Upgrade to Monitor
              </Button>
            </div>
          ) : (
            <div className="relative inline-flex flex-col items-stretch">
              {showAuditCredit && (
                <div className="absolute -top-3 right-0 z-10 rotate-6">
                  <AuditCreditBadge />
                </div>
              )}
              <Button type="button" variant="secondary" size="sm" asChild>
                <Link to={upgradeHref}>Upgrade to Monitor</Link>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
